import { motion } from 'framer-motion';
import { GraduationCap, Users, BookOpen, Award, Heart, UserCircle, Briefcase } from 'lucide-react';

const PORTAL_CONFIG = {
  parent: {
    title: 'Parent Portal',
    subtitle: 'For Parents & Guardians',
    icon: Heart,
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    iconBg: 'bg-amber-500',
    features: [
      { icon: BookOpen, text: "View Child's Progress", color: 'text-yellow-300' },
      { icon: Users, text: 'Track Attendance', color: 'text-orange-300' },
      { icon: Award, text: 'Exam Results', color: 'text-red-300' },
      { icon: Heart, text: 'Gate Pass Approvals', color: 'text-pink-300' },
    ]
  },
  student: {
    title: 'Student Portal',
    subtitle: 'For Students',
    icon: GraduationCap,
    gradient: 'from-blue-500 via-cyan-500 to-teal-500',
    iconBg: 'bg-blue-500',
    features: [
      { icon: BookOpen, text: 'View Assignments', color: 'text-cyan-300' },
      { icon: Award, text: 'Check Results', color: 'text-teal-300' },
      { icon: Users, text: 'Class Schedule', color: 'text-blue-300' },
      { icon: GraduationCap, text: 'Online Exams', color: 'text-sky-300' },
    ]
  },
  staff: {
    title: 'Staff Portal',
    subtitle: 'For Teachers & Staff',
    icon: Briefcase,
    gradient: 'from-emerald-500 via-green-500 to-teal-500',
    iconBg: 'bg-emerald-500',
    features: [
      { icon: Users, text: 'Manage Students', color: 'text-green-300' },
      { icon: BookOpen, text: 'Record Grades', color: 'text-emerald-300' },
      { icon: Award, text: 'Attendance', color: 'text-teal-300' },
      { icon: Briefcase, text: 'Self Service', color: 'text-lime-300' },
    ]
  },
  admin: {
    title: 'Admin Portal',
    subtitle: 'School Administration',
    icon: UserCircle,
    gradient: 'from-indigo-600 via-purple-600 to-pink-500',
    iconBg: 'bg-indigo-600',
    features: [
      { icon: GraduationCap, text: 'Student Management', color: 'text-blue-300' },
      { icon: Users, text: 'Staff & HR', color: 'text-green-300' },
      { icon: BookOpen, text: 'Academics & Exams', color: 'text-purple-300' },
      { icon: Award, text: 'Fee & Payments', color: 'text-amber-300' },
    ]
  }
};

const GradientLoginLayout = ({ portalType, children }) => {
  const config = PORTAL_CONFIG[portalType] || PORTAL_CONFIG.admin;
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Left Side - Gradient Background with Features */}
      <div className={`hidden lg:flex lg:w-1/2 relative bg-gradient-to-br ${config.gradient}`}>
        {/* Animated Background Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute -top-20 -left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="absolute -bottom-20 -right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [-20, 20, -20] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_school-hub-saas/artifacts/mh8ri9d5_CLOUD%20CAMPUS%20SCHOOLS.png" 
              alt="Cloud Campus Schools Logo"
              className="h-20 w-auto mb-8 drop-shadow-2xl"
            />
            <div className="flex items-center gap-3 mb-4">
              <div className={`${config.iconBg} p-3 rounded-xl`}>
                <Icon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{config.title}</h2>
                <p className="text-white/70 text-sm">{config.subtitle}</p>
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">
              Welcome to<br />
              <span className="text-5xl bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Cloud Campus
              </span>
            </h1>
            <p className="text-lg text-white/80 mb-10 max-w-md">
              The complete school management solution. Access your portal securely.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-2 gap-4"
          >
            {config.features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3"
                >
                  <FeatureIcon className={`h-5 w-5 ${feature.color}`} />
                  <span className="text-sm font-medium">{feature.text}</span>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-8 left-12 text-white/60 text-sm"
          >
            Trusted by 500+ schools across India
          </motion.div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-8 relative">
        {/* Mobile Logo */}
        <div className="lg:hidden mb-8 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_school-hub-saas/artifacts/mh8ri9d5_CLOUD%20CAMPUS%20SCHOOLS.png" 
            alt="Cloud Campus Schools Logo"
            className="h-12 w-auto mx-auto mb-4"
          />
          <div className="flex items-center justify-center gap-2">
            <div className={`${config.iconBg} p-2 rounded-lg`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold">{config.title}</span>
          </div>
        </div>

        {children}
      </div>
    </div>
  );
};

export default GradientLoginLayout;
